import type { Json } from '@/integrations/supabase/types';

export const DISPLAY_DETAIL_READ_ERROR = 'Display detail read unavailable';

export interface DisplayDetailOwnerProfile {
  user_id: string;
  display_name: string | null;
  full_name: string | null;
  avatar_url: string | null;
  bio: string | null;
  social_links: Json | null;
  is_placeholder: boolean;
}

export interface DisplayDetailRecentCheckin {
  user_id: string;
  display_name: string | null;
  avatar_url: string | null;
  checked_in_at: string;
}

export interface DisplayDetailRead {
  id: number;
  slug: string | null;
  name: string;
  description: string | null;
  location: string;
  city: string | null;
  state: string | null;
  latitude: number | null;
  longitude: number | null;
  image_url: string | null;
  photos: Json | null;
  display_type: string | null;
  holidays: string[] | null;
  holiday_type: string | null;
  /** Per-holiday show names, e.g. {"Christmas": "..."}; null when the display has one name. */
  holiday_names?: Record<string, string> | null;
  year_started: number | null;
  fm_station: string | null;
  /** speakers, fm or both; null when the owner has not said (needs migration 20261116100000). */
  audio_mode?: 'speakers' | 'fm' | 'both' | null;
  parking_other: string | null;
  light_count: number | null;
  software: string | null;
  controllers: string | null;
  controllers_short: string | null;
  last_confirmed_at: string | null;
  location_precision: 'exact' | 'approximate' | 'city';
  facebook: string | null;
  instagram: string | null;
  website: string | null;
  schedule: Json | null;
  tags: string[] | null;
  average_rating: number | null;
  rating_count: number | null;
  rating_distribution: Json | null;
  upvote_count: number;
  /** Publish state; "draft" means hidden from visitors. */
  status?: 'active' | 'draft' | 'archived';
  user_id: string | null;
  updated: string | null;
  updated_at: string;
  created_at: string;
  user_profile: DisplayDetailOwnerProfile | null;
  claimed_at: string | null;
  owner_verified: boolean;
  audio_url: string | null;
  remote_falcon_subdomain: string | null;
  remote_falcon_voting: boolean;
  /** Owner-set listening link (https, public domain name), or null. */
  listen_url: string | null;
  /** True when visitors can vote on songs through the Light-O-Rama Relay (voting on, Relay paired, not LOR Advanced). */
  lor_voting?: boolean;
  /** True while nobody real owns the display (no owner or an import stand-in). */
  claimable: boolean;
  /** Last five public check-ins, newest first. Empty when none or the read is unavailable. */
  recent_checkins: DisplayDetailRecentCheckin[];
}

export interface DisplayDetailReadResponse {
  success: true;
  display: DisplayDetailRead | null;
}

export function isDisplayDetailReadResponse(value: unknown): value is DisplayDetailReadResponse {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<DisplayDetailReadResponse>;
  if (candidate.success !== true) return false;
  if (candidate.display === null) return true;
  if (!candidate.display || typeof candidate.display !== 'object') return false;

  const display = candidate.display as Partial<DisplayDetailRead>;
  return (
    Number.isInteger(display.id) &&
    typeof display.name === 'string' &&
    typeof display.location === 'string' &&
    typeof display.updated_at === 'string' &&
    typeof display.created_at === 'string' &&
    (display.recent_checkins === undefined || Array.isArray(display.recent_checkins))
  );
}
